import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { appConfirmMessage } from '../../utils/appConfirm';
import { FaSearch } from 'react-icons/fa';
import { HiPencil } from 'react-icons/hi';
import { RiDeleteBinLine } from 'react-icons/ri';
import { LuCircleAlert } from 'react-icons/lu';
import { graphqlRequest } from '../../utils/graphqlRequest';
import ExpensesForm from './ExpensesForm';
import './styles/Expenses.css';

const EXPENSES_QUERY = `
  query GetFinanceExpenses {
    getFinanceExpenses {
      totalExpenses
      laborCosts
      equipmentCosts
      pendingPayments
      records {
        id
        date
        vendor
        category
        project
        amount
        status
        quantity
        itemCode
      }
    }
  }
`;

const DELETE_EXPENSE_MUTATION = `
  mutation DeleteFinanceExpense($id: String!) {
    deleteFinanceExpense(id: $id)
  }
`;

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function Expenses() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All Categories');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await graphqlRequest(EXPENSES_QUERY);
      setData(result.getFinanceExpenses);
    } catch (err) {
      console.error('Failed to fetch expenses data:', err);
      setError('Failed to load expenses data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const handleDelete = async (id) => {
    if (!await appConfirmMessage('Are you sure you want to delete this expense record?')) return;
    try {
      await graphqlRequest(DELETE_EXPENSE_MUTATION, { id });
      fetchExpenses();
    } catch (err) {
      console.error('Failed to delete expense:', err);
      toast.error('Failed to delete record. It may be a payslip record which cannot be deleted from here.');
    }
  };

  if (loading) {
    return (
      <div className="expenses-page">
        <div className="expenses-loading">
          <div className="spinner" />
          <p>Loading expenses data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="expenses-page">
        <div className="expenses-error">
          <LuCircleAlert size={40} />
          <p>{error}</p>
          <button onClick={fetchExpenses}>Retry</button>
        </div>
      </div>
    );
  }

  const records = data?.records || [];
  const categories = ['All Categories', ...new Set(records.map((r) => r.category))];

  const filtered = records.filter((e) => {
    const matchText = Object.values(e)
      .join(' ')
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchCategory =
      categoryFilter === 'All Categories' || e.category === categoryFilter;
    return matchText && matchCategory;
  });

  return (
    <div className="expenses-page">
      {/* ADD EXPENSES FORM MODAL */}
      <ExpensesForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={fetchExpenses}
      />

      {/* TITLE */}
      <div className="expenses-title">
        <h1>Expenses</h1>
        <p>Track and manage all business expenses</p>
      </div>

      {/* SUMMARY */}
      <div className="expenses-summary">
        <div className="expense-card">
          <p>Total Expenses</p>
          <h2>{formatCurrency(data?.totalExpenses)}</h2>
        </div>

        <div className="expense-card labor">
          <p>Worker Wages</p>
          <h2>{formatCurrency(data?.laborCosts)}</h2>
        </div>

        <div className="expense-card supplies">
          <p>Equipment Costs</p>
          <h2>{formatCurrency(data?.equipmentCosts)}</h2>
        </div>

        <div className="expense-card pending">
          <p>Pending Payments</p>
          <h2>{formatCurrency(data?.pendingPayments)}</h2>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="expenses-toolbar">
        <div className="expenses-search">
          <FaSearch />
          <input
            placeholder="Search by name, category, or project..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select
          className="category-filter"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          {categories.map((cat) => (
            <option key={cat}>{cat}</option>
          ))}
        </select>

        <button className="filter-btn">More Filters</button>
        <button className="add-expense-btn" onClick={() => setFormOpen(true)}>
          + Add Expense
        </button>
      </div>

      {/* TABLE */}
      <div className="expenses-table-card">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th>Item Code</th>
              <th>Category</th>
              <th>Project</th>
              <th>Qty</th>
              <th>Total Cost</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  No expense records found
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.id}>
                  <td>{row.date}</td>
                  <td>{row.vendor}</td>
                  <td>{row.itemCode || '—'}</td>
                  <td>{row.category}</td>
                  <td>{row.project}</td>
                  <td>{row.quantity != null ? row.quantity : '—'}</td>
                  <td>{formatCurrency(row.amount)}</td>
                  <td>
                    <span className={`status ${row.status.toLowerCase()}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="actions">
                    <HiPencil title="Edit" />
                    <RiDeleteBinLine
                      title="Delete"
                      onClick={() => handleDelete(row.id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
